"use client"

import { useState, useEffect, useMemo } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import dayjs from "dayjs"
import { Info, HelpCircle, AlertCircle } from "lucide-react"

import { Button } from "@/components/ui/button"
import { SalaryProgressionChart } from "@/components/salary-progression-chart"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Slider } from "@/components/ui/slider"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { formatCurrency} from "@/lib/utils"
import { apiClient} from "@/lib/api/api-client"
import { CalculationRequest, CalculationResponse } from "@/lib/api/api-types"

type CopyData = {
  validation: {
    startDateRequired: string
    mustBeZeroOrGreater: string
  }
  errors: {
    serviceUnavailable: string
  }
  form: {
    title: string
    labels: {
      carrier: string
      personnelType: string
      startDate: string
      flightsPerMonth: string
      narrowbody: string
      widebody: string
      approximateHours: string
    }
    placeholders: {
      selectCarrier: string
      selectPersonnelType: string
      zero: string
    }
    options: {
      mainline: string
      rouge: string
      flightAttendant: string
      serviceDirector: string
    }
    tooltips: {
      startDate: string
      narrowbodyFlights: string
      widebodyFlights: string
    }
    descriptions: {
      serviceDirectorHours: string
      mainlineFlightAttendantHours: string
      rougeFlightAttendantHours: string
    }
    sliderLabels: {
      minimum: string
      maximum: string
    }
  }
  buttons: {
    calculating: string
    calculatePay: string
  }
  results: {
    monthlyComparison: string
    yearlyComparison: string
    currentMonthlyPay: string
    newMonthlyPay: string
    currentYearlyPay: string
    newYearlyPay: string
    includesGroundPremium: string
  }
  information: {
    minimumGuaranteeTitle: string
    yearlyEstimateTitle: string
    guaranteeExplanations: {
      acServiceDirector: string
      acFlightAttendant: string
      rougeCrew: string
    }
    yearlyEstimateExplanation: string
  }
}

const defaultCopy: CopyData = {
  validation: {
    startDateRequired: "Please select a start date",
    mustBeZeroOrGreater: "Must be 0 or greater"
  },
  errors: {
    serviceUnavailable: "Service temporarily unavailable. Please try again later."
  },
  form: {
    title: "Cabin Crew Pay Calculator",
    labels: {
      carrier: "Carrier",
      personnelType: "Personnel Type",
      startDate: "Position Start Date",
      flightsPerMonth: "Flights per Month",
      narrowbody: "Narrowbody",
      widebody: "Widebody",
      approximateHours: "Approximate Credit Hours per Month"
    },
    placeholders: {
      selectCarrier: "Select carrier",
      selectPersonnelType: "Select personnel type",
      zero: "0"
    },
    options: {
      mainline: "Mainline",
      rouge: "Rouge",
      flightAttendant: "Flight Attendant",
      serviceDirector: "Service Director"
    },
    tooltips: {
      startDate: "This provides an estimate of your current step on the wage scale based on your position start date, but does not take into account absences from the payroll which can affect pay progression.",
      narrowbodyFlights: "A ground premium of 60 minutes is paid for each narrowbody flight boarded.",
      widebodyFlights: "A ground premium of 70 minutes is paid for each widebody flight boarded."
    },
    descriptions: {
      serviceDirectorHours: "Air Canada Mainline Service Directors typically receive 77 credit hours per month.",
      mainlineFlightAttendantHours: "Air Canada Mainline Flight Attendants typically receive 77 credit hours per month.",
      rougeFlightAttendantHours: "Air Canada Rouge Flight Attendants typically receive 83 credit hours per month."
    },
    sliderLabels: {
      minimum: "0",
      maximum: "120"
    }
  },
  buttons: {
    calculating: "Calculating...",
    calculatePay: "Calculate Pay"
  },
  results: {
    monthlyComparison: "Monthly Pay Comparison",
    yearlyComparison: "Yearly Pay Comparison",
    currentMonthlyPay: "Current Monthly Pay",
    newMonthlyPay: "New Monthly Pay",
    currentYearlyPay: "Current Yearly Pay",
    newYearlyPay: "New Yearly Pay",
    includesGroundPremium: "Includes Ground Premium: "
  },
  information: {
    minimumGuaranteeTitle: "Minimum Monthly Guarantee Applied",
    yearlyEstimateTitle: "Yearly Estimate",
    guaranteeExplanations: {
      acServiceDirector: "Air Canada Service Directors have a minimum monthly guarantee of 65 or 75 hours per month, depending on block type. In this calculation, 65 hours at narrowbody rate have been applied.",
      acFlightAttendant: "Air Canada cabin crew members have a minimum monthly guarantee of 65 or 75 hours per month, depending on block type. In this calculation, 65 hours were applied.",
      rougeCrew: "Air Canada Rouge cabin crew members have a minimum monthly guarantee of 75 or 80 hours per month, depending on block type. In this calculation, 75 hours were applied."
    },
    yearlyEstimateExplanation: "This yearly projection includes rate progression and annual contractual increases over the next 12 months. Calculations exclude per diems, absences, vacation rate increases, individual schedule variations, training periods, and additional monthly compensation such as draft premiums, duty overtime premiums, and ground duty claims."
  }
}

export function CalculatorForm() {
  const [copy, setCopy] = useState<CopyData>(defaultCopy)
  const [copyLoading, setCopyLoading] = useState(true)

  // Load copy data
  useEffect(() => {
    const loadCopy = async () => {
      try {
        const response = await fetch('/copy.json')
        if (response.ok) {
          const copyData = await response.json()
          setCopy(copyData)
        }
      } catch (error) {
        console.warn('Failed to load copy.json, using defaults')
      } finally {
        setCopyLoading(false)
      }
    }
    
    loadCopy()
  }, [])

  const formSchema = z.object({
    carrier: z.enum(["AC", "RV"]),
    personnelType: z.enum(["FA", "SD"]),
    startDate: z.date({
      message: copy.validation.startDateRequired,
    }),
    narrowbodyFlights: z.number().min(0, copy.validation.mustBeZeroOrGreater),
    widebodyFlights: z.number().min(0, copy.validation.mustBeZeroOrGreater),
    approxHours: z.number().min(0, copy.validation.mustBeZeroOrGreater).optional(),
    narrowbodyHours: z.number().min(0, copy.validation.mustBeZeroOrGreater).optional(),
    widebodyHours: z.number().min(0, copy.validation.mustBeZeroOrGreater).optional(),
  })

  type FormData = z.infer<typeof formSchema>

  const [result, setResult] = useState<CalculationResponse>({
    metadata: {
      timestamp: "",
      apiVersion: "",
      requestId: ""
    },
    request: {
      carrier: "AC",
      personnelType: "FA",
      startDate: "",
      narrowbodyFlights: 0,
      widebodyFlights: 0,
      approxHours: 0,
      narrowbodyHours: 0,
      widebodyHours: 0
    },
    current: {
      monthly: {
        old: {
          totalPay: 0,
          groundPremium: 0,
          baseHourlyRate: 0,
          guaranteeApplied: false
        },
        new: {
          totalPay: 0,
          groundPremium: 0,
          baseHourlyRate: 0,
          guaranteeApplied: false
        },
        difference: {
          amount: 0,
          percentage: 0
        }
      },
      yearly: {
        old: {
          totalPay: 0,
          groundPremium: 0
        },
        new: {
          totalPay: 0,
          groundPremium: 0
        },
        difference: {
          amount: 0,
          percentage: 0
        }
      }
    },
    projections: {
      yearlyProjections: [],
      monthlyProjections: []
    }
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string>("")

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      carrier: "AC",
      personnelType: "FA",
      startDate: undefined,
      narrowbodyFlights: 0,
      widebodyFlights: 0,
      approxHours: 77, // Default for AC FA to prevent race condition
      narrowbodyHours: 0,
      widebodyHours: 0,
    },
  })

  const carrier = form.watch("carrier")
  const personnelType = form.watch("personnelType")

  // Calculate dynamic slider ranges and defaults - memoized for performance
  const approxHoursConfig = useMemo(() => {
    if (carrier === "AC" && personnelType === "FA") {
      return { min: 64, max: 100, default: 77 }
    } else if (carrier === "RV" && personnelType === "FA") {
      return { min: 74, max: 120, default: 83 }
    }
    return { min: 0, max: 120, default: 0 }
  }, [carrier, personnelType])

  // Handle carrier/personnel type state management
  useEffect(() => {
    if (carrier === "RV" && personnelType === "SD") {
      form.setValue("personnelType", "FA")
    }
  }, [carrier, personnelType, form])

  // Handle personnel type switching with complete state management
  useEffect(() => {
    if (personnelType === "SD") {
      // Set SD values and clear FA fields
      form.setValue("narrowbodyHours", 39)
      form.setValue("widebodyHours", 39)
      form.setValue("approxHours", 0)
    } else if (personnelType === "FA") {
      // Set FA values and clear SD fields
      form.setValue("approxHours", approxHoursConfig.default)
      form.setValue("narrowbodyHours", 0)
      form.setValue("widebodyHours", 0)
    }
  }, [personnelType, carrier, form, approxHoursConfig])

  const isPersonnelTypeLocked = carrier === "RV"
  const showSplitHours = personnelType === "SD"

  const onSubmit = async (data: FormData) => {
    setIsLoading(true)
    setError("")
    
    try {
      // Create API request directly from form data
      const requestData: CalculationRequest = {
        carrier: data.carrier,
        personnelType: data.personnelType,
        startDate: dayjs(data.startDate).format('YYYY-MM-DD'),
        narrowbodyFlights: data.narrowbodyFlights,
        widebodyFlights: data.widebodyFlights,
        approxHours: data.approxHours ?? 0,
        narrowbodyHours: data.narrowbodyHours ?? 0,
        widebodyHours: data.widebodyHours ?? 0,
      }

      const apiResponse = await apiClient.calculatePay(requestData)
      setResult(apiResponse)

    } catch (error) {
      setError(copy.errors.serviceUnavailable)
    } finally {
      setIsLoading(false)
    }
  }

  if (copyLoading) {
    return (
      <div className="max-w-7xl mx-auto space-y-8">
        <Card className="w-full shadow-lg flex flex-col">
          <CardContent className="p-6 flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <TooltipProvider>
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="lg:grid lg:grid-cols-3 lg:gap-6 lg:items-stretch space-y-6 lg:space-y-0">
          <Card className="w-full shadow-lg flex flex-col">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-foreground">{copy.form.title}</CardTitle>
            </CardHeader> 
            <CardContent className="pt-0 flex-1 flex flex-col pb-6">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5 flex-1 flex flex-col">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Carrier Selection */}
                  <FormField
                    control={form.control}
                    name="carrier"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-foreground">{copy.form.labels.carrier}</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder={copy.form.placeholders.selectCarrier} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="AC">{copy.form.options.mainline}</SelectItem>
                            <SelectItem value="RV">{copy.form.options.rouge}</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Personnel Type Selection */}
                  <FormField
                    control={form.control}
                    name="personnelType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-foreground">{copy.form.labels.personnelType}</FormLabel>
                        <Select 
                          onValueChange={field.onChange} 
                          value={field.value}
                          disabled={isPersonnelTypeLocked}
                        >
                          <FormControl>
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder={copy.form.placeholders.selectPersonnelType} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="FA">{copy.form.options.flightAttendant}</SelectItem>
                            <SelectItem value="SD" disabled={isPersonnelTypeLocked}>
                              {copy.form.options.serviceDirector}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                              {/* Start Date */}
                <FormField
                  control={form.control}
                  name="startDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-foreground flex items-center gap-2">
                        {copy.form.labels.startDate}
                        <Tooltip>
                          <TooltipTrigger type="button">
                            <HelpCircle className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{copy.form.tooltips.startDate}</p>
                          </TooltipContent>
                        </Tooltip>
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          className="h-9"
                          value={field.value ? dayjs(field.value).format('YYYY-MM-DD') : ''}
                          onChange={(e) => {
                            const dateValue = e.target.value ? dayjs(e.target.value).toDate() : undefined
                            field.onChange(dateValue)
                          }}
                          max={dayjs().format('YYYY-MM-DD')}
                          min="1900-01-01"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Flights per Month Section */}
                <div className="space-y-3">
                  <div className="text-left">
                    <h3 className="text-sm font-medium text-foreground">{copy.form.labels.flightsPerMonth}</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Narrowbody Flights */}
                    <FormField
                      control={form.control}
                      name="narrowbodyFlights"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-medium text-foreground flex items-center gap-1.5">
                            {copy.form.labels.narrowbody}
                            <Tooltip>
                              <TooltipTrigger type="button">
                                <HelpCircle className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{copy.form.tooltips.narrowbodyFlights}</p>
                              </TooltipContent>
                            </Tooltip>
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min="0"
                              step="1"
                              placeholder={copy.form.placeholders.zero}
                              className="h-9"
                              value={field.value || ""}
                              onChange={(e) => {
                                const value = e.target.value === "" ? 0 : Number(e.target.value)
                                field.onChange(value)
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Widebody Flights */}
                    <FormField
                      control={form.control}
                      name="widebodyFlights"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-medium text-foreground flex items-center gap-1.5">
                            {copy.form.labels.widebody}
                            <Tooltip>
                              <TooltipTrigger type="button">
                                <HelpCircle className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{copy.form.tooltips.widebodyFlights}</p>
                              </TooltipContent>
                            </Tooltip>
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min="0"
                              step="1"
                              placeholder={copy.form.placeholders.zero}
                              className="h-9"
                              value={field.value || ""}
                              onChange={(e) => {
                                const value = e.target.value === "" ? 0 : Number(e.target.value)
                                field.onChange(value)
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

              {/* Hours Fields - Conditional based on personnel type */}
              {showSplitHours ? (
                <div className="space-y-3">
                  <div className="text-left">
                    <h3 className="text-sm font-medium text-foreground">{copy.form.labels.approximateHours}</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      {copy.form.descriptions.serviceDirectorHours}
                    </p>
                  </div>
                  <div className="space-y-4">
                    <FormField
                      control={form.control}
                      name="narrowbodyHours"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-medium text-foreground">{copy.form.labels.narrowbody}</FormLabel>
                          <FormControl>
                            <div className="space-y-2">
                              <Slider
                                min={0}
                                max={120}
                                step={1}
                                value={[field.value || 39]}
                                onValueChange={(value) => field.onChange(value[0])}
                                className="w-full"
                              />
                              <div className="flex justify-between items-center px-1">
                                <span className="text-xs text-muted-foreground">{copy.form.sliderLabels.minimum}</span>
                                <span className="text-sm font-semibold text-foreground">{field.value || 39}h</span>
                                <span className="text-xs text-muted-foreground">{copy.form.sliderLabels.maximum}</span>
                              </div>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="widebodyHours"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs font-medium text-foreground">{copy.form.labels.widebody}</FormLabel>
                          <FormControl>
                            <div className="space-y-2">
                              <Slider
                                min={0}
                                max={120}
                                step={1}
                                value={[field.value || 39]}
                                onValueChange={(value) => field.onChange(value[0])}
                                className="w-full"
                              />
                              <div className="flex justify-between items-center px-1">
                                <span className="text-xs text-muted-foreground">{copy.form.sliderLabels.minimum}</span>
                                <span className="text-sm font-semibold text-foreground">{field.value || 39}h</span>
                                <span className="text-xs text-muted-foreground">{copy.form.sliderLabels.maximum}</span>
                              </div>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <FormField
                    control={form.control}
                    name="approxHours"
                    render={({ field }) => (
                      <FormItem>
                        <div className="mb-3">
                          <FormLabel className="text-sm font-medium text-foreground">{copy.form.labels.approximateHours}</FormLabel>
                          <p className="text-xs text-muted-foreground mt-1">
                            {carrier === "AC" 
                              ? copy.form.descriptions.mainlineFlightAttendantHours
                              : copy.form.descriptions.rougeFlightAttendantHours
                            }
                          </p>
                        </div>
                        <FormControl>
                          <div className="space-y-2">
                            <Slider
                              min={approxHoursConfig.min}
                              max={approxHoursConfig.max}
                              step={1}
                              value={[field.value || approxHoursConfig.default]}
                              onValueChange={(value) => field.onChange(value[0])}
                              className="w-full"
                            />
                            <div className="flex justify-between items-center px-1">
                              <span className="text-xs text-muted-foreground">{approxHoursConfig.min}</span>
                              <span className="text-sm font-semibold text-foreground">{field.value || approxHoursConfig.default}h</span>
                              <span className="text-xs text-muted-foreground">{approxHoursConfig.max}</span>
                            </div>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

                {/* Error Display */}
                {error && (
                  <Card className="border-red-200 bg-red-50">
                    <CardContent className="p-3">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-red-600" />
                        <div>
                          <p className="text-sm text-red-800">{error}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <div className="mt-auto pt-3 pb-2">
                  <Button type="submit" className="w-full h-10" disabled={isLoading}>
                    {isLoading ? copy.buttons.calculating : copy.buttons.calculatePay}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>

        {/* Results Display */}
        <Card className="w-full flex flex-col shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-foreground">
              {copy.results.monthlyComparison}
              {(result.current.monthly.old.guaranteeApplied || result.current.monthly.new.guaranteeApplied) && (
                <sup className="text-sm opacity-70 ml-1">1</sup>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 flex-1 flex flex-col space-y-6">
            <div className="flex-1 flex flex-col justify-center items-center p-6 bg-neutral-50 rounded-lg border border-neutral-200 min-h-[120px]">
              <p className="text-sm font-medium text-neutral-600 mb-2">{copy.results.currentMonthlyPay}</p>
              <p className="text-3xl font-bold text-neutral-900">{formatCurrency(result.current.monthly.old.totalPay)}</p>
            </div>
            <div className="flex-1 flex flex-col justify-center items-center p-6 bg-neutral-50 rounded-lg border border-neutral-200 min-h-[120px]">
              <p className="text-sm font-medium text-neutral-600 mb-2">{copy.results.newMonthlyPay}</p>
              <p className="text-3xl font-bold text-neutral-900">{formatCurrency(result.current.monthly.new.totalPay)}</p>
              {result.current.monthly.new.groundPremium > 0 && (
                <div className="mt-2 text-center">
                  <p className="text-xs text-neutral-500">
                    {copy.results.includesGroundPremium}{formatCurrency(result.current.monthly.new.groundPremium)}
                  </p>
                </div>
              )}
              {result.current.monthly.difference.amount !== 0 && (
                <div className="mt-3 text-center">
                  <p className={`text-lg font-semibold ${result.current.monthly.difference.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(() => {
                      const amount = formatCurrency(result.current.monthly.difference.amount);
                      const percentage = result.current.monthly.difference.percentage.toFixed(1);
                      
                      if (result.current.monthly.difference.amount > 0) {
                        return '+' + amount + ' (+' + percentage + '%)';
                      } else {
                        return amount + ' (' + percentage + '%)';
                      }
                    })()}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Yearly Results Display */}
        <Card className="w-full flex flex-col shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-foreground">
              {copy.results.yearlyComparison}
              {(() => {
                const showGuaranteeNotice = result.current.monthly.old.guaranteeApplied || result.current.monthly.new.guaranteeApplied
                const showYearlyNotice = result.current.monthly.old.totalPay > 0 || result.current.monthly.new.totalPay > 0
                
                if (showYearlyNotice) {
                  return <sup className="text-sm opacity-70 ml-1">{showGuaranteeNotice ? 2 : 1}</sup>
                }
                return null
              })()}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 flex-1 flex flex-col space-y-6">
            <div className="flex-1 flex flex-col justify-center items-center p-6 bg-neutral-50 rounded-lg border border-neutral-200 min-h-[120px]">
              <p className="text-sm font-medium text-neutral-600 mb-2">{copy.results.currentYearlyPay}</p>
              <p className="text-3xl font-bold text-neutral-900">{formatCurrency(result.current.yearly.old.totalPay)}</p>
            </div>
            <div className="flex-1 flex flex-col justify-center items-center p-6 bg-neutral-50 rounded-lg border border-neutral-200 min-h-[120px]">
              <p className="text-sm font-medium text-neutral-600 mb-2">{copy.results.newYearlyPay}</p>
              <p className="text-3xl font-bold text-neutral-900">{formatCurrency(result.current.yearly.new.totalPay)}</p>
              {result.current.yearly.new.groundPremium > 0 && (
                <div className="mt-2 text-center">
                  <p className="text-xs text-neutral-500">
                    {copy.results.includesGroundPremium}{formatCurrency(result.current.yearly.new.groundPremium)}
                  </p>
                </div>
              )}
              {result.current.yearly.difference.amount !== 0 && (
                <div className="mt-3 text-center">
                  <p className={`text-lg font-semibold ${result.current.yearly.difference.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(() => {
                      const amount = formatCurrency(result.current.yearly.difference.amount);
                      const percentage = result.current.yearly.difference.percentage.toFixed(1);
                      
                      if (result.current.yearly.difference.amount > 0) {
                        return '+' + amount + ' (+' + percentage + '%)';
                      } else {
                        return amount + ' (' + percentage + '%)';
                      }
                    })()}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

      </div>

      {/* Information Panel */}
      {((result.current.monthly.old.guaranteeApplied || result.current.monthly.new.guaranteeApplied) || result.current.monthly.old.totalPay > 0 || result.current.monthly.new.totalPay > 0) && (
        <Card 
          className="border"
          style={{
            backgroundColor: 'var(--background-fill-information)',
            borderColor: 'var(--border-information)',
          }}
        >
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <Info 
                className="h-4 w-4 mt-0.5 flex-shrink-0" 
                style={{ color: 'var(--icon-information)' }}
              />
              <div className="space-y-3">
                {(() => {
                  const showGuaranteeNotice = result.current.monthly.old.guaranteeApplied || result.current.monthly.new.guaranteeApplied
                  const showYearlyNotice = result.current.monthly.old.totalPay > 0 || result.current.monthly.new.totalPay > 0
                  const currentSuperscript = 1

                  return (
                    <>
                      {/* Minimum Pay Guarantee Notice */}
                      {showGuaranteeNotice && (
                        <div>
                          <p 
                            className="font-semibold mb-1 text-sm"
                            style={{ color: 'var(--text-label)' }}
                          >
                            {copy.information.minimumGuaranteeTitle} <sup className="text-xs opacity-70">{currentSuperscript}</sup>
                          </p>
                          <p 
                            className="text-xs leading-relaxed"
                            style={{ color: 'var(--text-secondary)' }}
                          >
                            {(() => {
                              const carrier = result.request.carrier;
                              const personnelType = result.request.personnelType;
                              
                              // Air Canada Service Directors
                              if (carrier === "AC" && personnelType === "SD") {
                                  return copy.information.guaranteeExplanations.acServiceDirector
                              }
                              
                              // Air Canada Flight Attendants
                              if (carrier === "AC" && personnelType === "FA") {
                                  return copy.information.guaranteeExplanations.acFlightAttendant
                              }
                              
                              // Rouge cabin crew
                              if (carrier === "RV") {
                                  return copy.information.guaranteeExplanations.rougeCrew;
                              }
                            })()}
                          </p>
                        </div>
                      )}

                      {/* Yearly Pay Information Notice */}
                      {showYearlyNotice && (
                        <div>
                          <p 
                            className="font-semibold mb-1 text-sm"
                            style={{ color: 'var(--text-label)' }}
                          >
                            {copy.information.yearlyEstimateTitle} <sup className="text-xs opacity-70">{showGuaranteeNotice ? currentSuperscript + 1 : currentSuperscript}</sup>
                          </p>
                          <p 
                            className="text-xs leading-relaxed"
                            style={{ color: 'var(--text-secondary)' }}
                          >
                            {copy.information.yearlyEstimateExplanation}
                          </p>
                        </div>
                      )}
                    </>
                  )
                })()}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Salary Progression Chart */}
      {result.projections.yearlyProjections && result.projections.yearlyProjections.length > 0 && (
        <SalaryProgressionChart 
          data={result.projections.yearlyProjections} 
          detailedData={result.projections.monthlyProjections || []}
        />
      )}
    </div>
    </TooltipProvider>
  )
} 